import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldX, Eye, Phone, Bike, Clock, CheckCircle2, XCircle, User, MapPin,
} from "lucide-react";

export default function AdminRepartidoresTab() {
  const [pendientes, setPendientes] = useState([]);
  const [activos, setActivos] = useState([]);
  const [rechazados, setRechazados] = useState([]);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    try {
      const [p, a, r] = await Promise.all([
        api.get("/admin/repartidores?status_filter=pending_verification"),
        api.get("/admin/repartidores?status_filter=active"),
        api.get("/admin/repartidores?status_filter=rejected"),
      ]);
      setPendientes(p.data.repartidores);
      setActivos(a.data.repartidores);
      setRechazados(r.data.repartidores);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="overline text-zinc-500">Master Admin</div>
          <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter">Control de Repartidores</h2>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-amber-100 text-amber-700 border-0" data-testid="badge-pending-count">{pendientes.length} pendientes</Badge>
          <Badge className="bg-emerald-100 text-emerald-700 border-0" data-testid="badge-active-count">{activos.length} activos</Badge>
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="rounded-sm bg-white border border-zinc-200 p-1 h-11">
          <TabsTrigger value="pending" data-testid="rep-tab-pending" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">
            <Clock className="w-3.5 h-3.5 mr-1" /> Pendientes de Verificación ({pendientes.length})
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="rep-tab-active" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Repartidores Activos ({activos.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="rep-tab-rejected" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">
            <ShieldX className="w-3.5 h-3.5 mr-1" /> Rechazados ({rechazados.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <RepartidoresList lista={pendientes} onReview={setReviewing} emptyText="No hay repartidores pendientes de verificación." variante="pending" />
        </TabsContent>
        <TabsContent value="active" className="mt-4">
          <RepartidoresList lista={activos} onReview={setReviewing} emptyText="Aún no hay repartidores activos." variante="active" />
        </TabsContent>
        <TabsContent value="rejected" className="mt-4">
          <RepartidoresList lista={rechazados} onReview={setReviewing} emptyText="Sin repartidores rechazados." variante="rejected" />
        </TabsContent>
      </Tabs>

      <DocumentReviewModal
        rep={reviewing}
        onClose={() => setReviewing(null)}
        onAction={async () => { setReviewing(null); await load(); }}
      />
    </div>
  );
}

function RepartidoresList({ lista, onReview, emptyText, variante }) {
  if (lista.length === 0) {
    return (
      <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-14 text-center text-zinc-500 text-sm">
        {emptyText}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {lista.map(r => (
        <div key={r.id} data-testid={`rep-row-${r.id}`} className="bg-white border border-zinc-200 rounded-sm p-4 flex items-center gap-4 flex-wrap">
          <img
            src={r.foto_selfie_url || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200"}
            alt={r.nombre}
            className="w-14 h-14 rounded-full object-cover border border-zinc-200"
          />
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold tracking-tight">{r.nombre}</div>
            <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.telefono || "—"}</span>
              <span className="flex items-center gap-1"><Bike className="w-3 h-3" />{r.tipo_vehiculo || r.vehiculo}</span>
              {r.ciudad && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.ciudad}, {r.estado_mx}</span>}
            </div>
            {variante === "rejected" && r.motivo_rechazo && (
              <div className="mt-2 text-xs bg-red-50 border border-red-200 text-red-700 rounded-sm px-2 py-1">
                Motivo: {r.motivo_rechazo}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid={`review-${r.id}`}
              onClick={() => onReview(r)}
              className="rounded-sm h-9"
            >
              <Eye className="w-4 h-4 mr-1" /> Revisar Documentos
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentReviewModal({ rep, onClose, onAction }) {
  const [rejectMode, setRejectMode] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  if (!rep) return null;
  const status = rep.status;

  const aprobar = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/repartidores/${rep.id}/aprobar`);
      toast.success(`${rep.nombre} aprobado. Ya puede aceptar entregas.`);
      onAction();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const rechazar = async () => {
    if (!motivo.trim()) { toast.error("Escribe un motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/repartidores/${rep.id}/rechazar`, { motivo });
      toast.success("Repartidor rechazado. Se le notificará el motivo.");
      onAction();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const docs = [
    { label: "INE / Pasaporte", url: rep.doc_ine_url },
    { label: "Licencia de conducir", url: rep.doc_licencia_url },
    { label: "Tarjeta de circulación", url: rep.doc_tarjeta_circulacion_url },
    { label: "Comprobante de domicilio", url: rep.doc_comprobante_domicilio_url },
  ];

  return (
    <Dialog open={!!rep} onOpenChange={(v) => { if (!v) { setRejectMode(false); setMotivo(""); onClose(); } }}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Revisión de documentos</DialogTitle>
        </DialogHeader>

        {/* Perfil */}
        <div className="flex items-center gap-4 pb-4 border-b border-zinc-200">
          <img
            src={rep.foto_selfie_url || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200"}
            alt={rep.nombre}
            className="w-24 h-24 rounded-full object-cover border-2 border-[#FF3366]"
          />
          <div className="flex-1">
            <div className="font-display font-black text-2xl tracking-tighter">{rep.nombre}</div>
            <div className="text-sm text-zinc-600 mt-1 space-y-0.5">
              <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" /> {rep.email || "—"} · Nacimiento: {rep.fecha_nacimiento || "—"}</div>
              <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {rep.telefono || "—"} · <Bike className="w-3.5 h-3.5" /> {rep.tipo_vehiculo || rep.vehiculo}</div>
              {rep.banco && <div>Banco: <b>{rep.banco}</b> · CLABE: <span className="font-mono text-xs">••••{rep.clabe?.slice(-4)}</span></div>}
              {rep.ciudad && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {rep.ciudad}, {rep.estado_mx}</div>}
            </div>
            <Badge className={`mt-2 border-0 ${
              status === "pending_verification" ? "bg-amber-100 text-amber-700" :
              status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
            }`} data-testid="modal-status-badge">
              {status === "pending_verification" ? "PENDIENTE" : status === "active" ? "ACTIVO" : "RECHAZADO"}
            </Badge>
          </div>
        </div>

        {/* Documentos side-by-side */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {docs.map(d => (
            <div key={d.label} data-testid={`doc-${d.label}`} className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="p-2 bg-zinc-50 text-xs font-medium border-b border-zinc-200">{d.label}</div>
              <div className="aspect-[3/2] bg-zinc-100">
                {d.url ? (
                  <a href={d.url} target="_blank" rel="noreferrer">
                    <img src={d.url} alt={d.label} className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                  </a>
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-400 text-xs">Sin documento</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {rep.motivo_rechazo && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-800 rounded-sm p-3 text-sm">
            <b>Motivo anterior de rechazo:</b> {rep.motivo_rechazo}
          </div>
        )}

        {/* Acciones — solo si está pendiente */}
        {status === "pending_verification" && !rejectMode && (
          <div className="mt-4 pt-4 border-t border-zinc-200 flex gap-3 flex-wrap">
            <Button
              data-testid="btn-aprobar"
              onClick={aprobar}
              disabled={busy}
              className="flex-1 h-12 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" /> Aprobar Repartidor
            </Button>
            <Button
              data-testid="btn-mostrar-rechazo"
              onClick={() => setRejectMode(true)}
              disabled={busy}
              variant="outline"
              className="flex-1 h-12 rounded-sm border-red-300 text-red-700 hover:bg-red-50 font-display font-bold"
            >
              <XCircle className="w-5 h-5 mr-2" /> Rechazar Repartidor
            </Button>
          </div>
        )}
        {status === "pending_verification" && rejectMode && (
          <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3">
            <label className="text-sm font-medium">Motivo del rechazo</label>
            <Input
              data-testid="input-motivo"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Ej: INE borrosa, Licencia vencida…"
              className="rounded-sm h-11"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setRejectMode(false); setMotivo(""); }} className="rounded-sm">Cancelar</Button>
              <Button data-testid="btn-confirmar-rechazo" onClick={rechazar} disabled={busy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white flex-1">
                {busy ? "Rechazando…" : "Confirmar rechazo"}
              </Button>
            </div>
          </div>
        )}
        {status === "rejected" && (
          <div className="mt-4 pt-4 border-t border-zinc-200">
            <Button data-testid="btn-reactivar" onClick={aprobar} disabled={busy} className="w-full h-12 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white">
              <CheckCircle2 className="w-5 h-5 mr-2" /> Reactivar como Activo
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
